import { Loader2 } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import type { AdminConfig } from '../config';
import {
  answerNewPassword,
  answerTotp,
  cognitoMessage,
  confirmForgotPassword,
  forgotPassword,
  signIn,
  type AdminSession,
  type SignInStep,
} from '../lib/cognito';
import { Field, btnPrimary, inputCls } from './ui';

type Challenge = Extract<SignInStep, { challenge: string }>;
type View =
  | { kind: 'credentials' }
  | { kind: 'totp'; step: Challenge }
  | { kind: 'new_password'; step: Challenge }
  | { kind: 'forgot' }
  | { kind: 'reset'; email: string };

export const PASSWORD_HINT = 'Almeno 12 caratteri, con maiuscole, minuscole, numeri e simboli.';
const linkCls = 'text-sm underline hover:text-fg-hover';

/**
 * Accesso al pannello (docs/07-admin-e-bot.md §1): email e password, poi, se servono, la password
 * nuova (primo accesso dopo l'invito) e il codice dell'app di autenticazione. Recupero della
 * password con il codice via email.
 */
export function SignIn({
  config,
  onSignedIn,
}: {
  config: AdminConfig;
  onSignedIn: (s: AdminSession) => void;
}) {
  const [view, setView] = useState<View>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const go = (next: View) => {
    setError(null);
    setCode('');
    setNewPassword('');
    setView(next);
  };
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(cognitoMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const follow = (step: SignInStep) => {
    if (step.kind === 'done') onSignedIn(step.session);
    else go({ kind: step.kind, step });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    void run(async () => {
      switch (view.kind) {
        case 'credentials':
          return follow(await signIn(config, email.trim(), password));
        case 'totp':
          return follow(await answerTotp(config, view.step, code.trim()));
        case 'new_password':
          return follow(await answerNewPassword(config, view.step, newPassword));
        case 'forgot':
          await forgotPassword(config, email.trim());
          go({ kind: 'reset', email: email.trim() });
          return setNotice('Se l’indirizzo è registrato, ti abbiamo mandato un codice via email.');
        case 'reset':
          await confirmForgotPassword(config, view.email, code.trim(), newPassword);
          setPassword('');
          go({ kind: 'credentials' });
          return setNotice('Password cambiata: ora entra con quella nuova.');
      }
    });
  };

  const titles: Record<View['kind'], string> = {
    credentials: 'Accedi al pannello',
    totp: 'Codice di verifica',
    new_password: 'Scegli una password',
    forgot: 'Password dimenticata',
    reset: 'Nuova password',
  };

  let fields: ReactNode;
  let action = 'Continua';
  switch (view.kind) {
    case 'credentials':
      action = 'Accedi';
      fields = (
        <>
          <Field label="Email">
            <input
              type="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
          </Field>
        </>
      );
      break;
    case 'totp':
      action = 'Verifica';
      fields = (
        <Field label="Codice a 6 cifre" hint="Lo trovi nell’app di autenticazione del telefono.">
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className={`${inputCls} text-center font-mono text-lg tracking-[0.4em]`}
          />
        </Field>
      );
      break;
    case 'new_password':
      action = 'Salva ed entra';
      fields = (
        <>
          <p className="text-sm text-muted">
            È il primo accesso: sostituisci la password temporanea dell’invito.
          </p>
          <NewPasswordField value={newPassword} onChange={setNewPassword} />
        </>
      );
      break;
    case 'forgot':
      action = 'Mandami il codice';
      fields = (
        <Field label="Email">
          <input
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
        </Field>
      );
      break;
    case 'reset':
      action = 'Cambia la password';
      fields = (
        <>
          <Field label="Codice ricevuto via email">
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
              className={inputCls}
            />
          </Field>
          <NewPasswordField value={newPassword} onChange={setNewPassword} />
        </>
      );
      break;
  }

  return (
    <main className="mx-auto max-w-[420px] p-6">
      <img src="/logo.svg" alt="Salento Sagre Eventi" className="mt-16 h-8 w-auto dark:invert" />
      <h1 className="mt-6 font-display text-h1">{titles[view.kind]}</h1>
      {view.kind === 'credentials' && (
        <p className="mt-2 text-muted">
          L’accesso è riservato alla redazione di Salento Sagre Eventi.
        </p>
      )}
      <form onSubmit={submit} className="mt-6 flex flex-col gap-4" noValidate={false}>
        {fields}
        {notice && (
          <p role="status" className="bg-ok-soft p-3 text-sm">
            {notice}
          </p>
        )}
        {error && (
          <p role="alert" className="bg-danger-soft p-3 text-sm">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy && <Loader2 className="size-4 animate-spin" aria-hidden />}
          {action}
        </button>
      </form>
      <div className="mt-4 flex flex-wrap gap-4">
        {view.kind === 'credentials' ? (
          <button type="button" onClick={() => go({ kind: 'forgot' })} className={linkCls}>
            Password dimenticata?
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              setNotice(null);
              go({ kind: 'credentials' });
            }}
            className={linkCls}
          >
            Torna all’accesso
          </button>
        )}
      </div>
    </main>
  );
}

export function NewPasswordField({
  label = 'Password nuova',
  value,
  onChange,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label} hint={PASSWORD_HINT}>
      <input
        type="password"
        autoComplete="new-password"
        required
        minLength={12}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
      />
    </Field>
  );
}
